/* eslint-disable no-undef */
import PropTypes from 'prop-types';
import { cloneElement, Component, createRef } from 'react';
import { PortalWithState } from 'react-portal';
import styled from 'styled-components';
import { v4 as uuid } from 'uuid';

import { ScreenReadersContent } from '@hivebrite/design-system/ScreenReadersContent';

import VisualViewport from 'hb-react/shared/components/VisualViewport';
import onEnterOrSpacePress from 'hb-react/shared/utils/onEnterOrSpacePress';
import * as mobileDeviceFromUserAgent from 'helpers/mobileDeviceFromUserAgent';

const sentinelStyle = {
  width: 0,
  height: 0,
  overflow: 'hidden',
  outline: 'none',
};

/**
 * @deprecated please use `import ModalWithTemplate from 'hb-react/shared/components/DesignSystem/Modal/ModalWithTemplate';` instead
 */
class ContentPreview extends Component {
  constructor(props) {
    super(props);
    this.labelledById = uuid();
    this.viewportRef = createRef();
  }

  componentDidMount() {
    const { portalProps } = this.props;

    if (portalProps.defaultOpen) {
      this.handlePortalOpen();
    }
  }

  componentWillUnmount() {
    document.removeEventListener('click', this.handleMouseClickOutside);
  }

  handleScroll = (e) => {
    const { onScrollHandler } = this.props;

    if (onScrollHandler) {
      if (typeof onScrollHandler !== 'function') {
        throw new Error('onScrollHandler props must be a function.');
      } else if (
        $(e.target).scrollTop() + $(e.target).innerHeight() >=
        $(e.target)[0].scrollHeight
      ) {
        onScrollHandler();
      }
    }
  };

  handleMouseClickOutside = (e) => {
    if (e?.target?.classList?.contains?.('modal-preview__overlay')) {
      e.stopPropagation();
      this.portal.closePortal();
    }
  };

  handleCloseButtonClick = (e) => {
    e.preventDefault();
    this.portal.closePortal();
  };

  handlePortalOpen = () => {
    const { onOpen, closeOnOutsideClick } = this.props.portalProps;

    // Remember las focused element
    this.lastActiveElementRef = document.activeElement;

    this.sentinelStartRef?.focus();

    if (closeOnOutsideClick) {
      document.addEventListener('click', this.handleMouseClickOutside);
    }

    onOpen?.();
  };

  handlePortalClose = () => {
    const { onClose } = this.props.portalProps;

    document.removeEventListener('click', this.handleMouseClickOutside);

    // Put focus back on the last active element if any.
    this.lastActiveElementRef?.focus({ preventScroll: true });
    this.lastActiveElementRef = null;

    onClose?.();
  };

  handleWrapperKeyDown = (e) => {
    // Keep focus inside dialog
    if (e.key === 'Tab') {
      const isNext = !e.shiftKey;
      const { activeElement } = document;
      const { sentinelStartRef, sentinelEndRef } = this;

      if (isNext && activeElement === sentinelEndRef) {
        sentinelStartRef.focus();
      } else if (!isNext && activeElement === sentinelStartRef) {
        sentinelEndRef.focus();
      }
    }
  };

  header = () => {
    const { title, screenReadersTitle, subtitle } = this.props;

    if (title) {
      return (
        <header className="modal-preview__header">
          <h2
            id={screenReadersTitle ? undefined : this.labelledById}
            className="h2 flush hard"
          >
            {title}
          </h2>
          {subtitle ? <span>{subtitle}</span> : null}
        </header>
      );
    }

    return null;
  };

  renderChildren = (portal) => {
    const { children } = this.props;

    if (typeof children === 'function') {
      return cloneElement(children({ portal }));
    }

    return cloneElement(children);
  };

  render() {
    const {
      modalPreviewViewStyle,
      portalProps,
      style,
      overlayClass,
      size,
      fullSize,
      title,
      screenReadersTitle,
      screenReadersCloseButtonText,
      modalPreviewContainerClassNames = '',
    } = this.props;

    return (
      <PortalWithState
        {...portalProps}
        onOpen={this.handlePortalOpen}
        onClose={this.handlePortalClose}
        ref={(ref) => (this.portal = ref)}
      >
        {(portal) => (
          <>
            {portalProps.openByClickOn &&
              cloneElement(portalProps.openByClickOn, {
                onClick: portal.openPortal,
              })}
            <>
              {portal.portal(
                <div
                  onKeyDown={this.handleWrapperKeyDown}
                  className={`modal-preview-view soft-half--top soft-half--sides${
                    fullSize ? ' modal-preview-view--full' : ''
                  }`}
                  role="dialog"
                  aria-modal="true"
                  aria-labelledby={
                    !(title || screenReadersTitle)
                      ? undefined
                      : this.labelledById
                  }
                  tabIndex={-1}
                  style={modalPreviewViewStyle}
                >
                  <div
                    className={`fixed north west south one-whole modal-preview__overlay ${overlayClass}`}
                  />
                  <div
                    tabIndex={0}
                    ref={(ref) => {
                      this.sentinelStartRef = ref;
                    }}
                    style={sentinelStyle}
                    aria-hidden="true"
                  />
                  {screenReadersTitle ? (
                    <ScreenReadersContent
                      key="a11y"
                      as="h2"
                      id={this.labelledById}
                    >
                      {screenReadersTitle}
                    </ScreenReadersContent>
                  ) : null}
                  <button
                    type="button"
                    className="close-modal-preview color-white js-close-modal-preview"
                    aria-label={screenReadersCloseButtonText}
                    onMouseDown={this.handleCloseButtonClick}
                    onKeyDown={onEnterOrSpacePress(this.handleCloseButtonClick)}
                  >
                    <i className="icon-multiply icon-size--100" />
                  </button>
                  <div
                    className={`modal-preview__container modal-preview__container--${size} ${modalPreviewContainerClassNames}`}
                    onScroll={this.handleScroll}
                    style={style}
                    role="document"
                  >
                    <div
                      className="modal-preview__content"
                      data-testid="content-preview-modal"
                    >
                      {mobileDeviceFromUserAgent.any() ? (
                        <ScrollWrapper
                          data-scroll-lock-scrollable
                          ref={this.viewportRef}
                          wrapScroll
                        >
                          {this.header()}
                          {this.renderChildren(portal)}
                        </ScrollWrapper>
                      ) : (
                        <>
                          {this.header()}
                          {this.renderChildren(portal)}
                        </>
                      )}
                    </div>
                  </div>
                  <div
                    tabIndex={0}
                    ref={(ref) => {
                      this.sentinelEndRef = ref;
                    }}
                    style={sentinelStyle}
                    aria-hidden="true"
                  />
                </div>,
              )}
            </>
          </>
        )}
      </PortalWithState>
    );
  }
}

ContentPreview.propTypes = {
  fullSize: PropTypes.bool,
  portalProps: PropTypes.object.isRequired,
  children: PropTypes.oneOfType([PropTypes.element, PropTypes.func]).isRequired,
  overlayClass: PropTypes.string,
  onScrollHandler: PropTypes.func,
  size: PropTypes.string,
  style: PropTypes.object,
  title: PropTypes.string,
  screenReadersTitle: PropTypes.string,
  screenReadersCloseButtonText: PropTypes.string,
  modalPreviewContainerClassNames: PropTypes.string,
};

ContentPreview.defaultProps = {
  fullSize: false,
  overlayClass: '',
  size: '',
  style: {},
  modalPreviewViewStyle: {},
  modalPreviewContainerClassNames: '',
  screenReadersCloseButtonText: __('Close window'),
};

export default ContentPreview;

const ScrollWrapper = styled(VisualViewport)`
  -webkit-overflow-scrolling: touch;
  overflow-y: auto;
`;
